import React, { useCallback, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ListView from 'components/ListView/index';

import { Box, CircularProgress } from '@material-ui/core';
import Filters from 'components/Filters';
import iconAdd from 'images/icon_add_new.svg';
import Portal from 'portal/Portal';
import { BASE_PANEL, FORM, LIST_VIEW } from 'utils/constants/css';
import { useLocalStorage } from 'utils/helper/useLocalStorage.jsx';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    position: 'relative',
    background: theme.palette.background.paper,
  },
  paper: {
    padding: theme.spacing(0),
    textAlign: 'center',
    color: theme.palette.primary.main,
  },
  shiftIcon: {
    position: 'absolute',
    zIndex: 10,
    top: '49%',
    left: '-10px',
    borderRadius: '25%',
    cursor: 'pointer',
    padding: 0,
    fontSize: '10px',
    background: theme.palette.primary.main,
    height: '2rem',
    width: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  formPaper: {
    position: 'absolute',
    top: 5,
    left: 0,
    zIndex: 9,
    width: '100%',
    height: FORM.HEIGHT,
  },
  listView: {
    height: LIST_VIEW.HEIGHT,
    display: 'flex',
    flexDirection: 'column',
  },
  basePanel: {
    marginLeft: BASE_PANEL.MARGIN_LEFT,
    marginRight: BASE_PANEL.MARGIN_RIGHT,
    marginTop: BASE_PANEL.MARGIN_TOP,
    background: theme.palette.background.default,
    boxShadow: BASE_PANEL.BOX_SHADOW,
  },
  mapContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
    boxShadow: '-0.5px 1px 3px -1px rgba(0,0,0,0.75);',
    zIndex: 99999999,
  },
  mySvgStyle: {
    cursor: 'pointer',
    color: theme.palette.secondary.light,
  },
}));

const LayoutWithMap = ({
  cols = [],
  onEdit,
  loading = false,
  toggleModal,
  editable,
  //start filters
  filtersRequired,
  filters,
  setFilters,
  defaultFilterValues,
  filterValues,
  // end filters
  onUpdate,
  header,
  data = [],
  DetailComponent,
  FormComponent,
  operationComponent,
  enhancedCancellation,
  downloadable,
  onDownloadClick,
  fetchData,
  rowModelType,
  pagination,
  forceRefresh,
  setForceRefresh,
  totalEntries,
  createTextHover,
  downloadHoverText,
  setGridAPI,
  fitColumnsToSize,
  enhancedOnSelect,
  uploadable,
  onUploadClick,
  uploadHoverText,
  limitTags,
  filterKey,
  persistentStateName,
  showListView,
  DashboardComponent,
  forceOpenDetailPanel, // open detail panel through bool variable
  linkedFilterFields,
  showSearchFilter,
  selectedID,
}) => {
  const classes = useStyles();
  const [picked, setPicked] = useState();
  const [detailOpen, setDetailOpen] = useState(false);
  const [agGridApi, setApi] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [filterReset, setFilterReset] = useState(false);
  const [detailOpenState, setDetailsOpenState] = useLocalStorage(
    `${persistentStateName}_detailOpen`,
    false,
  );
  const [editModeState, setEditModeState] = useLocalStorage(
    `${persistentStateName}_editMode`,
    false,
  );

  const [pickedDataState, setPickedDataState] = useLocalStorage(
    `${persistentStateName}_picked`,
    {},
  );

  const toggleEditMode = () => {
    setEditMode(true);
    if (persistentStateName) {
      setEditModeState(() => true);
      setDetailsOpenState(() => true);
    }
  };

  const reset = () => {
    setFilterReset(true);
    // This is triggering the request fetchlist again. Might need it again
    // onUpdate(defaultFilterValues);
  };
  const onSelect = useCallback(
    vals => {
      const selectedData = vals.api.getSelectedRows();
      setPicked(selectedData[0]);
      if (persistentStateName) {
        setDetailsOpenState(prev => !prev);
        setPickedDataState(() => ({ ...selectedData[0] }));
      }
      setDetailOpen(true);
      enhancedOnSelect &&
        typeof enhancedOnSelect === 'function' &&
        enhancedOnSelect(selectedData[0]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agGridApi, persistentStateName, enhancedOnSelect],
  );

  //this is to fetch the updated data when the list data is getting updated from detail panel
  const getPickedData = () => {
    return data.find(
      singleListData =>
        singleListData.id === (picked?.id || pickedDataState?.id),
    );
  };
  const cancel = () => {
    if (persistentStateName) {
      setEditModeState(() => false);
      setDetailsOpenState(() => false);
      setPickedDataState({});
    }
    if (enhancedCancellation) {
      enhancedCancellation();
    }
    if (typeof agGridApi?.deselectAll === 'function') {
      agGridApi?.deselectAll();
    }
    setDetailOpen(false);
    setEditMode(false);
  };

  const closeForm = () => {
    if (persistentStateName) {
      setEditModeState(() => false);
      setDetailsOpenState(() => false);
      setPickedDataState({});
    }
    setEditMode(false);
    toggleModal();
  };

  const openDetailPanel =
    forceOpenDetailPanel ||
    (picked &&
      (detailOpen || detailOpenState) &&
      getPickedData() !== undefined);

  return (
    <div className={classes.root}>
      <Grid container spacing={1}>
        {DashboardComponent && <DashboardComponent />}
        <Grid item xs={6} sm={12} xl={12}>
          {onEdit && (
            <Box component={'div'} className={classes.formPaper}>
              {loading && (
                <Portal id={'portal'}>
                  <CircularProgress />
                </Portal>
              )}
              {
                <FormComponent
                  id={
                    picked || pickedDataState
                      ? picked?.id || pickedDataState?.id
                      : undefined
                  }
                  editMode={editMode || editModeState}
                  closeForm={closeForm}
                  persistentStateName={persistentStateName}
                  closeDetail={cancel} //ability to close detail panel from form component
                />
              }
            </Box>
          )}
        </Grid>

        <Box component={'div'} width={'100%'} className={classes.basePanel}>
          {filtersRequired && (
            <Filters
              id={`${header}_filter`}
              editable={editable}
              reset={reset}
              filters={filters}
              filterValues={filterValues}
              setFilters={setFilters}
              icon={iconAdd}
              onIconClick={toggleModal}
              header={header}
              onUpdate={onUpdate}
              downloadable={downloadable}
              onDownloadClick={onDownloadClick}
              createTextHover={createTextHover}
              downloadHoverText={downloadHoverText}
              uploadable={uploadable}
              onUploadClick={onUploadClick}
              uploadHoverText={uploadHoverText}
              defaultFilterValues={defaultFilterValues}
              limitTags={limitTags}
              linkedFilterFields={linkedFilterFields}
              showSearchFilter={showSearchFilter}
            />
          )}
          {showListView && (
            <Grid item xs={6} sm={12} xl={12}>
              <Box component={'div'} className={classes.listView}>
                <ListView
                  agGridApi={agGridApi}
                  setApi={setApi}
                  onSelect={onSelect}
                  rowData={data}
                  columns={cols}
                  fetchData={fetchData}
                  rowModelType={rowModelType}
                  pagination={pagination}
                  forceRefresh={forceRefresh}
                  setForceRefresh={setForceRefresh}
                  totalEntries={totalEntries}
                  setGridAPI={setGridAPI}
                  fitColumnsToSize={fitColumnsToSize}
                  filterKey={filterKey}
                  isFilterReset={filterReset}
                  setFilterReset={setFilterReset}
                  filters={filterValues}
                />

                {openDetailPanel && (
                  <DetailComponent
                    openDrawer={detailOpen || detailOpenState}
                    id={picked?.id || pickedDataState?.id || selectedID}
                    selected={getPickedData()}
                    cancel={cancel}
                    editable={editable}
                    toggleEditMode={toggleEditMode}
                    operationComponent={operationComponent}
                    downloadable={downloadable}
                  />
                )}
              </Box>
            </Grid>
          )}
        </Box>
      </Grid>
    </div>
  );
};
LayoutWithMap.defaultProps = {
  filtersRequired: true,
  showListView: true,
};
LayoutWithMap.propTypes = {
  header: PropTypes.string,
  filters: PropTypes.object,
  cols: PropTypes.array,
  data: PropTypes.array,
  DetailComponent: PropTypes.elementType,
  FormComponent: PropTypes.elementType,
  DashboardComponent: PropTypes.elementType,
  operationComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  filterValues: PropTypes.object,
  filtersRequired: PropTypes.bool,
  setFilters: PropTypes.func,
  editable: PropTypes.bool,
  loading: PropTypes.bool,
  filterKey: PropTypes.bool,
  onUpdate: PropTypes.func,
  onEdit: PropTypes.bool,
  toggleModal: PropTypes.func,
  enhancedCancellation: PropTypes.func,
  downloadable: PropTypes.bool,
  onDownloadClick: PropTypes.func,
  uploadable: PropTypes.bool,
  onUploadClick: PropTypes.func,
  persistentStateName: PropTypes.string,
  uploadHoverText: PropTypes.string,
  fetchData: PropTypes.func,
  rowModelType: PropTypes.string,
  pagination: PropTypes.bool,
  showListView: PropTypes.bool,
  forceRefresh: PropTypes.bool,
  setForceRefresh: PropTypes.func,
  totalEntries: PropTypes.number,
  createTextHover: PropTypes.string,
  downloadHoverText: PropTypes.string,
  setGridAPI: PropTypes.func,
  fitColumnsToSize: PropTypes.bool,
  forceOpenDetailPanel: PropTypes.bool,
  showSearchFilter: PropTypes.bool,
  enhancedOnSelect: PropTypes.func,
  defaultFilterValues: PropTypes.object,
  limitTags: PropTypes.number,
  dashboardSplitSize: PropTypes.string,
  selectedID: PropTypes.string,
  linkedFilterFields: PropTypes.object,
};
export default LayoutWithMap;
